import * as React from "react";
import { useId } from "@floating-ui/react";
import { MediaQuery } from "react-responsive";
import { Badge } from "../../badge/badge.mjs";
import { Collapse } from "../../collapse/collapse/collapse.mjs";
import { CollapseProvider } from "../../collapse/collapse-provider/collapse-provider.mjs";
import { CollapseTrigger } from "../../collapse/collapse-trigger/collapse-trigger.mjs";
import { Grid } from "../../grid/grid/grid.mjs";
import { GridColumn } from "../../grid/grid-column/grid-column.mjs";
import "debounce";
import "tabbable";
import { useCurrentBreakPoint } from "../../helpers/helper/hooks/use-current-breakpoint.mjs";
import { useIsMounted } from "../../helpers/helper/hooks/use-is-mounted.mjs";
import { Icon } from "../../icon/icon/icon.mjs";
import { useLabels } from "../../helpers/label-provider/use-labels.mjs";
import { useTheme } from "../../helpers/theme-provider/use-theme.mjs";
import { ProgressCategory } from "../progress-category/progress-category.mjs";
import { ProgressFooter } from "../progress-footer/progress-footer.mjs";
import { ProgressItem } from "../progress-item/progress-item.mjs";
const Progress = (props) => {
  var _a;
  const [isOpen, setIsOpen] = React.useState(false);
  const isMounted = useIsMounted();
  const { media } = useTheme();
  const currentBreak = useCurrentBreakPoint();
  const isMobile = currentBreak === "xs" || currentBreak === "sm";
  const labels = useLabels();
  const collapseId = (_a = useId()) != null ? _a : "";
  const BEM = () => {
    const classArray = ["progress"];
    if (props.break && props.style !== "card") {
      classArray.push("has-break-".concat(props.break));
    }
    if (props.style) {
      classArray.push("progress--".concat(props.style));
    }
    if (props.isBottom || !isMounted) {
      classArray.push("progress--bottom");
    }
    if (props.className) {
      classArray.push(props.className);
    }
    return classArray.join(" ");
  };
  const getBreakpoint = () => {
    const { break: breakPoint = "xs" } = props;
    return media.width[breakPoint].min;
  };
  const getContent = () => {
    let content = null;
    React.Children.forEach(props.children, (child) => {
      if (typeof child === "string" || typeof child === "number" || child.type === ProgressCategory || child.type === ProgressFooter) {
        return;
      }
      if (child.props.state === "current") {
        content = child.props.children;
      }
    });
    if (props.isBottom || !isMounted || props.style === "card") {
      return /* @__PURE__ */ React.createElement("div", { className: "progress__content" }, content);
    }
    if (isMounted && props.style !== "card") {
      return /* @__PURE__ */ React.createElement(MediaQuery, { minWidth: getBreakpoint() }, (matches) => setContent(matches, content));
    }
  };
  const setContent = (matches, content) => {
    if (matches) {
      return /* @__PURE__ */ React.createElement("div", { className: "progress__content" }, content);
    } else {
      return null;
    }
  };
  const getValidChildren = () => {
    return React.Children.toArray(props.children).filter((child) => {
      return React.isValidElement(child) && child.props.type !== "summary";
    });
  };
  const getNavItems = () => {
    let itemIndex = 0;
    return getValidChildren().map((child) => {
      if (React.isValidElement(child) && child.type === ProgressItem) {
        itemIndex += 1;
        return React.cloneElement(child, {
          break: props.break,
          isBottom: props.isBottom || !isMounted,
          style: props.style,
          index: itemIndex
        });
      }
      return child;
    });
  };
  const getProgressSteps = () => {
    if (!isMobile && props.style === "card") {
      return /* @__PURE__ */ React.createElement("div", { className: "progress__card-nav" }, /* @__PURE__ */ React.createElement("div", { className: "progress__card-nav-container" }, /* @__PURE__ */ React.createElement("ul", { className: "progress__steps" }, getNavItems())));
    }
    return /* @__PURE__ */ React.createElement("ul", { className: "progress__steps" }, getNavItems());
  };
  const indicatorItemBEM = (state) => {
    const classArray = ["progress__indicator-list-item"];
    if (state) {
      classArray.push("is-".concat(state));
    }
    return classArray.join(" ");
  };
  const progressDotBEM = (purpose, state) => {
    const classArray = ["progress__dot"];
    if (purpose) {
      classArray.push("progress__dot--".concat(purpose));
    }
    if (state) {
      classArray.push("progress__dot--".concat(state));
    }
    return classArray.join(" ");
  };
  const getProgressIndicatorArray = () => {
    const indicatorArray = [];
    getValidChildren().filter((child) => {
      return React.isValidElement(child) && child.type === ProgressItem;
    }).forEach((child, index) => {
      if (React.isValidElement(child)) {
        const { icon, purpose, state, subItems } = child.props;
        if (icon) {
          indicatorArray.push({ icon, purpose, state });
        } else if (subItems) {
          const subitemsArray = subItems.map((subItem, i) => {
            return {
              counter: "".concat(index + 1, ".").concat(i + 1),
              purpose: subItem.purpose,
              state: subItem.state
            };
          });
          indicatorArray.push(...subitemsArray);
        } else {
          indicatorArray.push({
            counter: index + 1,
            purpose,
            state
          });
        }
      }
    });
    return indicatorArray;
  };
  const renderIndicatorDots = () => getProgressIndicatorArray().map((item, index) => {
    const getBadgeTextOrIcon = () => {
      if (item.icon) {
        return { icon: item.icon };
      }
      return { text: item.counter || "" };
    };
    return /* @__PURE__ */ React.createElement("span", { className: indicatorItemBEM(item.state), key: index }, /* @__PURE__ */ React.createElement("span", { className: "progress__indicator-step-wrapper" }, item.state === "current" ? /* @__PURE__ */ React.createElement(Badge, { purpose: item.purpose === "employee" ? "employee-active" : "selected", size: "large", width: "fixed", ...getBadgeTextOrIcon() }) : /* @__PURE__ */ React.createElement("span", { className: "progress__dot-background" }, /* @__PURE__ */ React.createElement("span", { className: progressDotBEM(item.purpose, item.state) }))));
  });
  const indicatorIconBEM = () => {
    const classArray = ["progress__indicator-list-icon"];
    if (isOpen) {
      classArray.push("is-open");
    }
    return classArray.join(" ");
  };
  const getProgressStepsWithIndicatorDots = () => /* @__PURE__ */ React.createElement(CollapseProvider, { id: collapseId, isVisible: isOpen, onToggle: () => setIsOpen(!isOpen) }, /* @__PURE__ */ React.createElement(CollapseTrigger, null, /* @__PURE__ */ React.createElement(CollapseButton, { label: isOpen ? labels.get("close") : labels.get("open") }, /* @__PURE__ */ React.createElement(Grid, { element: "span", noWrap: true, align: ["middle-xs"], gutter: "none" }, /* @__PURE__ */ React.createElement(GridColumn, { width: ["max"] }, /* @__PURE__ */ React.createElement("span", { className: "progress__indicator-list", "aria-hidden": "true" }, renderIndicatorDots())), /* @__PURE__ */ React.createElement(GridColumn, { width: ["min"] }, /* @__PURE__ */ React.createElement(Icon, { modifiers: "#arrow-down", className: indicatorIconBEM(), size: "sm" }))))), /* @__PURE__ */ React.createElement(Collapse, { noContainer: true }, getProgressSteps()));
  const getCardProgress = () => {
    return /* @__PURE__ */ React.createElement("div", { className: BEM() }, isMobile ? getProgressStepsWithIndicatorDots() : getProgressSteps(), getContent());
  };
  const getProgress = () => {
    return /* @__PURE__ */ React.createElement("div", { className: BEM() }, getProgressSteps(), (props.break || props.isBottom) && getContent());
  };
  return /* @__PURE__ */ React.createElement(React.Fragment, null, props.style === "card" ? getCardProgress() : getProgress());
};
const CollapseButton = ({ label, children, url, ...rest }) => {
  return /* @__PURE__ */ React.createElement("button", { ...rest, type: "button", className: "progress__collapse-button" }, /* @__PURE__ */ React.createElement("span", { className: "sr-only" }, label), children);
};
export {
  Progress
};
